import type { BasePWAEventParams } from '@gh/common/models'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch, type ComputedRef } from 'vue'
import createLogger from '@/utils/logger'
import { pwaConfig } from '@/utils/pwaTools'
import { useAuthStore } from './auth'
import { doc, updateDoc } from 'firebase/firestore'
import { firestore } from '@/firebase'
import { useCustomerStore } from './customer'
import { useAnalyticsStore } from './analytics'

const PWA_INSTALL_DISMISSED_KEY = 'pwa-install-dismissed'
const PWA_INSTALL_DISMISSED_COUNT_KEY = 'pwa-install-dismissed-count'
const PWA_INSTALL_DISMISSED_DISABLED_KEY = 'pwa-install-dismissed-disabled'
const PWA_LAUNCHED_TRACKED_KEY = 'pwa-launched-tracked'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export enum PromptToShow {
  None = 0,
  InstallAndroid = 1,
  InstallIosInstructions = 2,
  InstallOnMobileInstructions = 3,
  InstallAndroidInstructions = 4,
}

const logger = createLogger('stores/pwa.ts')

/**
 * Helper function to get platform info for analytics
 */
function getPlatformInfo(): BasePWAEventParams {
  if (pwaConfig.other.isAndroidChrome) {
    return { platform: 'android', browser: 'chrome' }
  }
  if (pwaConfig.other.isIOSBrowserSafari) {
    return { platform: 'ios', browser: 'safari' }
  }
  if (pwaConfig.browserInfo.type === 'desktop') {
    return { platform: 'desktop', browser: pwaConfig.browserInfo.name }
  }
  return { platform: 'other', browser: pwaConfig.browserInfo.name }
}

export const usePWAStore = defineStore('pwa', () => {
  const authStore = useAuthStore()
  const customerStore = useCustomerStore()
  const analyticsStore = useAnalyticsStore()
  const { customer, impersonatedCustomerId } = storeToRefs(customerStore)
  const { allowPWA } = storeToRefs(authStore)

  const showInstallPromptOnAndroid = ref(false)
  const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
  const runningInStandalone = pwaConfig.runningInStandaloneMode

  const homePagePromptIsDismissed = ref(false)
  const isInitialized = ref(false)

  const pwaInstallationEnabled = computed(() => {
    return !pwaConfig.isDisabled && allowPWA.value
  })

  // Check if user has dismissed the prompt (stored in localStorage)
  const checkDismissed = () => {
    const dismissed = localStorage.getItem(PWA_INSTALL_DISMISSED_KEY)

    const dismissedDisabled =
      (localStorage.getItem(PWA_INSTALL_DISMISSED_DISABLED_KEY) || 'false') === 'true'
    if (dismissedDisabled) {
      logger.log('checkDismissed dismissed disabled')
      return false
    }
    if (dismissed) {
      const dismissedCount = parseInt(
        localStorage.getItem(PWA_INSTALL_DISMISSED_COUNT_KEY) || '0',
        10,
      )
      const dismissedIntervals = [7, 14]
      const dismissedTime = parseInt(dismissed, 10)
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24)
      // Show again after 7 days
      const dismissedInterval = dismissedIntervals[dismissedCount] || 365
      if (daysSinceDismissed < dismissedInterval) {
        homePagePromptIsDismissed.value = true
        logger.log(`checkDismissed days since dismissed < ${dismissedInterval} days ago`)
        return true
      }
    }
    return false
  }

  const handleBeforeInstallPrompt = (e: Event) => {
    toastDebug('handleBeforeInstallPrompt')
    // Prevent the default browser install prompt
    e.preventDefault()

    // Store the event for later use
    deferredPrompt.value = e as BeforeInstallPromptEvent
    logger.log('handleBeforeInstallPrompt deferred prompt stored')

    // Only show if not already installed and not recently dismissed
    if (!runningInStandalone) {
      showInstallPromptOnAndroid.value = true
      logger.log('handleBeforeInstallPrompt show install prompt')

      // Track install prompt shown event only when the prompt will be displayed
      if (!homePagePromptIsDismissed.value) {
        const platformInfo = getPlatformInfo()
        analyticsStore.events.trackPWAInstallPromptShown({
          ...platformInfo,
          display_mode: pwaConfig.displayMode,
        })
      }
    } else {
      logger.log('handleBeforeInstallPrompt show install prompt false, installed, dismissed')
    }
  }

  const handleAppInstalled = () => {
    // App was installed
    showInstallPromptOnAndroid.value = false
    deferredPrompt.value = null

    // Clear any dismissed state
    localStorage.removeItem(PWA_INSTALL_DISMISSED_KEY)
    localStorage.removeItem(PWA_INSTALL_DISMISSED_COUNT_KEY)
    logger.log('handleAppInstalled is installed, show install prompt false')

    // Track PWA installed event
    const platformInfo = getPlatformInfo()
    analyticsStore.events.trackPWAInstalled({
      ...platformInfo,
      display_mode: pwaConfig.displayMode,
      user_agent: navigator.userAgent,
    })
  }

  const installApp = async () => {
    // log('installApp', [])
    if (!deferredPrompt.value) {
      logger.log('installApp no deferred prompt available')
      return
    }

    try {
      logger.log('installApp before prompt true, prompt shown true')
      // Show the install prompt
      await deferredPrompt.value.prompt()
      logger.log('installApp prompt shown true')

      // Wait for the user to respond
      const { outcome } = await deferredPrompt.value.userChoice
      logger.log('installApp outcome', outcome)

      // Track install prompt response
      const platformInfo = getPlatformInfo()
      analyticsStore.events.trackPWAInstallPromptResponse({
        outcome,
        ...platformInfo,
      })

      if (outcome === 'accepted') {
        // User accepted the install prompt
        showInstallPromptOnAndroid.value = false
        deferredPrompt.value = null
        logger.log('installApp result accepted, show install prompt false')
      } else {
        // User dismissed the install prompt
        // Store dismissal in localStorage
        showInstallPromptOnAndroid.value = false
        await dismissPrompt()
        logger.log('installApp result dismissed, is dismissed true')
      }
    } catch (error) {
      logger.error('installApp error', error)
    }
  }

  const updateCustomerPwaInfoDismissedAt = async () => {
    if (!customer.value?.id) {
      return
    }
    await updateDoc(doc(firestore, 'customers', customer.value.id), {
      [`pwaInfo.homePagePromptDismissedAt`]: new Date(),
    })
  }

  const dismissPrompt = async () => {
    // Store dismissal in localStorage
    localStorage.setItem(PWA_INSTALL_DISMISSED_KEY, Date.now().toString())
    const dismissedCount = parseInt(
      localStorage.getItem(PWA_INSTALL_DISMISSED_COUNT_KEY) || '-1',
      10,
    )
    localStorage.setItem(PWA_INSTALL_DISMISSED_COUNT_KEY, (dismissedCount + 1).toString())
    homePagePromptIsDismissed.value = true
    await updateCustomerPwaInfoDismissedAt()
  }

  const clearPromptDismissed = () => {
    localStorage.removeItem(PWA_INSTALL_DISMISSED_KEY)
    localStorage.removeItem(PWA_INSTALL_DISMISSED_COUNT_KEY)
    homePagePromptIsDismissed.value = false
  }

  const init = () => {
    // Prevent multiple initializations
    if (isInitialized.value) {
      logger.log('init already initialized, skipping')
      return
    }
    isInitialized.value = true

    // Check if already installed
    if (runningInStandalone) {
      // Show uninstall option if installed
      logger.log('init installed true, show uninstall option true')

      // Track PWA launched event (check if first launch)
      const isFirstLaunch = !localStorage.getItem(PWA_LAUNCHED_TRACKED_KEY)
      localStorage.setItem(PWA_LAUNCHED_TRACKED_KEY, 'true')

      const platformInfo = getPlatformInfo()
      analyticsStore.events.trackPWALaunched({
        ...platformInfo,
        display_mode: pwaConfig.displayMode,
        is_first_launch: isFirstLaunch,
      })

      return
    }

    // Check if recently dismissed
    homePagePromptIsDismissed.value = checkDismissed()
    if (homePagePromptIsDismissed.value) {
      logger.log('init dismissed true')
      // Still add listeners in case user wants to install later
    }
    // Always add listeners - beforeinstallprompt can fire on desktop too
    // The event will only fire if the PWA meets installability criteria
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    // Listen for app installed event
    window.addEventListener('appinstalled', handleAppInstalled)
    if (window.beforeinstallpromptEvent) {
      logger.log('init beforeinstallpromptEvent', window.beforeinstallpromptEvent)
      handleBeforeInstallPrompt(window.beforeinstallpromptEvent)
    }
    logger.log(
      'init event listeners added true, note',
      'beforeinstallprompt will fire automatically when PWA is installable',
    )
  }

  const promptToShow: ComputedRef<PromptToShow> = computed(() => {
    if (!pwaInstallationEnabled.value) {
      return PromptToShow.None
    }
    if (!isInitialized.value || homePagePromptIsDismissed.value) {
      return PromptToShow.None
    }

    if (Object.keys(customer.value?.pwaInfo || {}).length > 0) {
      return PromptToShow.None
    }

    if (
      pwaConfig.other.isAndroidChrome &&
      showInstallPromptOnAndroid.value &&
      !runningInStandalone
    ) {
      return PromptToShow.InstallAndroid
    } else if (pwaConfig.promptChangeBrowserAndroid && !runningInStandalone) {
      return PromptToShow.InstallAndroidInstructions
    } else if (pwaConfig.promptSafariInstructionsIOS && !runningInStandalone) {
      return PromptToShow.InstallIosInstructions
    } else if (pwaConfig.promptInstallOnMobileInstructions) {
      return PromptToShow.InstallOnMobileInstructions
    } else {
      return PromptToShow.None
    }
  })

  watch([customer, impersonatedCustomerId], async (newVal) => {
    const [customerValueNew, impersonatedCustomerIdValueNew] = newVal
    if (!customerValueNew) {
      return
    }

    if (impersonatedCustomerIdValueNew) {
      return
    }

    if (!runningInStandalone) {
      return
    }

    const userAgent = navigator.userAgent

    interface PWAInfoUpdate {
      userAgent: string
      installedAt: Date
      disabled: boolean
      browserInfo: {
        type: string
        name: string
        os: string
        version: string
      }
    }

    let updates: PWAInfoUpdate | null = null
    const key =
      (pwaConfig.other.isAndroidChrome &&
        customerValueNew.pwaInfo?.android?.userAgent !== userAgent &&
        'android') ||
      (pwaConfig.other.isIOSBrowserSafari &&
        customerValueNew.pwaInfo?.ios?.userAgent !== userAgent &&
        'ios')

    if (key) {
      updates = {
        userAgent,
        installedAt: new Date(),
        disabled: pwaConfig.isDisabled,
        browserInfo: {
          type: pwaConfig.other.isIPad ? 'iPad' : pwaConfig.browserInfo.type || '',
          name: pwaConfig.browserInfo.name || '',
          os: pwaConfig.browserInfo.os || '',
          version: pwaConfig.browserInfo.version || '',
        },
      }
    }

    if (updates) {
      await updateDoc(doc(firestore, 'customers', customerValueNew.id), {
        [`pwaInfo.${key}`]: updates,
      })
    }
  })

  return {
    init,
    // destroy,
    // enableNotifications,
    installApp,
    dismissPrompt,
    clearPromptDismissed,
    showInstallPromptOnAndroid,
    isInstalled: ref(runningInStandalone),
    isInitialized,
    homePagePromptIsDismissed,
    deferredPrompt,
    promptToShow,
    pwaInstallationEnabled,
  }
})
